/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/App.jsx
 * Description  : Root component, auth state, routing
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Dashboard from './pages/Dashboard';
import Records from './pages/Records';
import History from './pages/History';
import Report from './pages/Report';
import Reconciliation from './pages/Reconciliation';
import ReconciliationHistory from './pages/ReconciliationHistory';
import Notifications from './pages/Notifications';
import Users from './pages/Users';
import Companies from './pages/Companies';
import Settings from './pages/Settings';
import Manual from './pages/Manual';
import Login from './pages/Login';
import { getSettings, getRecords, saveSettings } from './api';

// Toast context
export const ToastContext = createContext(null);
export function useToast() { return useContext(ToastContext); }

function ToastContainer({ toasts }) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast-enter pointer-events-auto px-4 py-3 rounded-lg shadow-lg text-white text-sm max-w-xs
            ${t.type === 'success' ? 'bg-success' : t.type === 'error' ? 'bg-danger' : 'bg-primary'}`}
        >
          <div className="font-semibold">{t.title || (t.type === 'success' ? 'สำเร็จ' : t.type === 'error' ? 'เกิดข้อผิดพลาด' : 'แจ้งเตือน')}</div>
          {t.message && <div className="text-xs mt-0.5 opacity-90">{t.message}</div>}
        </div>
      ))}
    </div>
  );
}

const PAGE_TITLES = {
  '/':              'Dashboard',
  '/records':       'รายการสำรองจ่าย',
  '/history':       'ประวัติการดำเนินการ',
  '/report':        'รายงาน',
  '/reconciliation': 'ตรวจสอบยอด SAP & Bank',
  '/reconciliation/history': 'รายงานตรวจสอบยอด SAP & Bank',
  '/notifications': 'การแจ้งเตือน',
  '/companies':     'บริษัท',
  '/users':         'ผู้ใช้งาน',
  '/settings':      'ตั้งค่าระบบ',
  '/manual':        'คู่มือการใช้งาน',
};

const PAGE_SUBTITLES = {
  '/':              'CSAF · Charoensin Alert Finance',
  '/records':       'จัดการและติดตามการสำรองจ่ายทั้งหมด',
  '/history':       'บันทึกการดำเนินงานและการแจ้งเตือนย้อนหลัง',
  '/report':        'สรุปและส่งออกรายงานการสำรองจ่าย',
  '/reconciliation': 'จับคู่รายการ SAP กับ Bank Statement อัตโนมัติ',
  '/reconciliation/history': 'รายงานที่บันทึกไว้ แยกตามช่วงเวลา/เดือน',
  '/notifications': 'ตั้งค่าการแจ้งเตือนและ Auto Mode',
  '/companies':     'จัดการบริษัทและกำหนดสิทธิ์พนักงาน',
  '/users':         'จัดการบัญชีผู้ใช้งานในระบบ',
  '/settings':      'ตั้งค่า SMTP และระบบแจ้งเตือน',
  '/manual':        'คู่มือการใช้งานระบบ CSAF',
};

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('csaf_user')); } catch { return null; }
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(getStoredUser);
  const [autoMode, setAutoMode] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, message, title) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, message, title }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const handleLogin = useCallback((user) => {
    setCurrentUser(user);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('csaf_token');
    localStorage.removeItem('csaf_user');
    setCurrentUser(null);
  }, []);

  const toggleAutoMode = useCallback(async () => {
    const newVal = autoMode ? '0' : '1';
    try {
      await saveSettings({ auto_mode: newVal });
      setAutoMode(!autoMode);
      addToast('success', `Auto Mode ${!autoMode ? 'เปิด' : 'ปิด'}แล้ว`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'ไม่สามารถบันทึกการตั้งค่าได้';
      addToast('error', msg);
    }
  }, [autoMode, addToast]);

  const refreshAppState = useCallback(async () => {
    try {
      const [settRes, recRes] = await Promise.all([getSettings(), getRecords('0')]);
      setAutoMode(settRes.data.auto_mode === '1');
      setPendingCount(recRes.data.length);
    } catch (e) {
      console.error('[App] App state refresh error:', e.message || e);
    }
  }, []);

  useEffect(() => {
    if (currentUser) refreshAppState();
  }, [currentUser, refreshAppState]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      <BrowserRouter basename="/">
        {!currentUser ? (
          <Login onLogin={handleLogin} />
        ) : (
          <div className="flex h-screen overflow-hidden bg-gray-50">
            <Sidebar pendingCount={pendingCount} notifCount={notifCount} currentUser={currentUser} />
            <div className="flex flex-col flex-1 overflow-hidden">
              <Topbar
                pageTitles={PAGE_TITLES}
                pageSubtitles={PAGE_SUBTITLES}
                autoMode={autoMode}
                onToggleAutoMode={toggleAutoMode}
                onRefresh={refreshAppState}
                currentUser={currentUser}
                onLogout={handleLogout}
              />
              <main className="flex-1 overflow-y-auto p-6">
                <Routes>
                  <Route path="/" element={<Dashboard onRefresh={refreshAppState} setPendingCount={setPendingCount} />} />
                  <Route path="/records" element={<Records onRefresh={refreshAppState} setPendingCount={setPendingCount} />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/report" element={<Report />} />
                  <Route path="/reconciliation" element={<Reconciliation />} />
                  <Route path="/reconciliation/history" element={<ReconciliationHistory />} />
                  <Route path="/companies" element={<Companies currentUser={currentUser} />} />
                  <Route path="/users" element={<Users currentUser={currentUser} />} />
                  <Route path="/notifications" element={currentUser?.role === 'admin' ? <Notifications setNotifCount={setNotifCount} /> : <Navigate to="/" replace />} />
                  <Route path="/settings" element={currentUser?.role === 'admin' ? <Settings autoMode={autoMode} setAutoMode={setAutoMode} /> : <Navigate to="/" replace />} />
                  <Route path="/manual" element={<Manual />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
            </div>
          </div>
        )}
        <ToastContainer toasts={toasts} />
      </BrowserRouter>
    </ToastContext.Provider>
  );
}
